import React, {useEffect} from 'react';
import {FormControl, makeStyles} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import ButtonField from '@components/system/ButtonField/ButtonField';
import {APP_PRIMARY_COLOR} from 'src/style/variable';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import DateInputField from '@components/system/FormField/DateInputField';
import './PharmacyClaimsPatientSearchSidebar.scss';

export interface PharmacyClaimsPatientSearchValues {
  last_name: string;
  first_name: string;
  account_number: string;
  guarantor_first_name: string;
  guarantor_last_name: string;
  date_of_service: string;
}

const emptyValues: PharmacyClaimsPatientSearchValues = {
  last_name: '',
  first_name: '',
  account_number: '',
  guarantor_first_name: '',
  guarantor_last_name: '',
  date_of_service: ''
};

const useStyles = makeStyles(() => ({
  searchButton: {
    height: '2.3rem',
    backgroundColor: APP_PRIMARY_COLOR,
    border: 'none',
    color: 'white',
    '&:hover': {
      backgroundColor: APP_PRIMARY_COLOR
    }
  }
}));

interface PharmacyClaimsPatientSearchSidebarProps {
  values: PharmacyClaimsPatientSearchValues;
  onApply: (values: PharmacyClaimsPatientSearchValues) => void;
  onClear: () => void;
  disabled?: boolean;
}

const PharmacyClaimsPatientSearchSidebar = (
  props: PharmacyClaimsPatientSearchSidebarProps
): React.ReactElement => {
  const {values, onApply, onClear, disabled} = props;
  const {t} = useTranslation();
  const classes = useStyles();
  const {enqueueSnackbar} = useSnackbar();
  const {control, handleSubmit, reset, errors} = useForm<PharmacyClaimsPatientSearchValues>({
    mode: 'all',
    defaultValues: values
  });

  useEffect(() => {
    reset(values);
  }, [values, reset]);

  const formElements = [
    {
      id: 'last_name',
      name: 'last_name' as const,
      label: t('CLAIMS.FORM_LABELS.LAST_NAME'),
      placeholder: t('CLAIMS.FORM_PLACEHOLDERS.LAST_NAME')
    },
    {
      id: 'first_name',
      name: 'first_name' as const,
      label: t('CLAIMS.FORM_LABELS.FIRST_NAME'),
      placeholder: t('CLAIMS.FORM_PLACEHOLDERS.FIRST_NAME')
    },
    {
      id: 'account_number',
      name: 'account_number' as const,
      label: t('CLAIMS.FORM_LABELS.ACCOUNT_NUMBER'),
      placeholder: t('CLAIMS.FORM_PLACEHOLDERS.ACCOUNT_NUMBER')
    },
    {
      id: 'guarantor_last_name',
      name: 'guarantor_last_name' as const,
      label: t('CLAIMS.FORM_LABELS.GUARANTOR_LAST_NAME'),
      placeholder: t('CLAIMS.FORM_PLACEHOLDERS.GUARANTOR_LAST_NAME')
    },
    {
      id: 'guarantor_first_name',
      name: 'guarantor_first_name' as const,
      label: t('CLAIMS.FORM_LABELS.GUARANTOR_FIRST_NAME'),
      placeholder: t('CLAIMS.FORM_PLACEHOLDERS.GUARANTOR_FIRST_NAME')
    }
  ];

  const handleFormSubmit = (formValues: PharmacyClaimsPatientSearchValues): void => {
    const filterValues: Partial<PharmacyClaimsPatientSearchValues> = {};
    (Object.keys(formValues) as Array<keyof PharmacyClaimsPatientSearchValues>).forEach(
      key => {
        const v = formValues[key];
        if (v) {
          filterValues[key] = v;
        }
      }
    );
    if (Object.keys(filterValues).length === 0) {
      enqueueSnackbar(
        t('Please enter at least 1 field value to get the results.'),
        SnackBarConfig('e')
      );
      return;
    }
    onApply({
      ...emptyValues,
      ...filterValues
    } as PharmacyClaimsPatientSearchValues);
  };

  return (
    <div className="pharmacy-claims-patient-search-sidebar">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {formElements.map(item => (
          <div key={item.id} className="form-element">
            <div className="form-element-label">{item.label}</div>
            <FormControl fullWidth variant="outlined">
              <Controller
                id={item.id}
                as={StyledOutlinedInput}
                margin="dense"
                control={control}
                name={item.name}
                autoComplete="on"
                placeholder={item.placeholder}
                disabled={disabled}
              />
            </FormControl>
          </div>
        ))}
        <div className="form-element">
          <div className="form-element-label">
            {t('CLAIMS.FORM_LABELS.DATE_OF_SERVICE')}
          </div>
          <FormControl fullWidth variant="outlined">
            <Controller
              control={control}
              name="date_of_service"
              render={({onChange, value}) => (
                <DateInputField
                  value={value || null}
                  onChange={(dateValue: string) => onChange(dateValue || '')}
                  placeholder={t('CLAIMS.FORM_PLACEHOLDERS.DATE_OF_SERVICE')}
                  error={errors.date_of_service}
                  disabled={disabled}
                />
              )}
            />
          </FormControl>
        </div>
        <div className="sidebar-actions">
          <ButtonField
            onClick={handleSubmit(handleFormSubmit)}
            buttonText={t('CLAIMS.SEARCH')}
            type="submit"
            disabled={disabled}
            className={[classes.searchButton, 'sidebar-search-button'].join(' ')}
          />
          <ButtonField
            onClick={() => {
              reset(emptyValues);
              onClear();
            }}
            buttonText={t('CLAIMS.CLEAR_ALL')}
            type="button"
            disabled={disabled}
            className="sidebar-clear-button"
          />
        </div>
      </form>
    </div>
  );
};

export default PharmacyClaimsPatientSearchSidebar;
export {emptyValues as emptyPharmacyClaimsPatientSearchValues};
